import * as React from "react";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

/**
 * Hook for managing search state with automatic change tracking
 * Provides both input value and debounced search query
 */
export function useSearchState(scope: string, debounceMs: number = 300) {
  // Store the actual search query in sessionStorage (tracked automatically)
  const [searchQuery, setSearchQuery] = useTrackedSessionStorage<string>(
    `${scope}::query`,
    "",
  );

  // Store the debounced query separately for backend queries
  const [debouncedSearchQuery, setDebouncedSearchQuery] =
    useTrackedSessionStorage<string>(`${scope}::debounced`, "");

  // Local state for the input value (for immediate UI updates)
  const [searchInputValue, setSearchInputValue] = React.useState(searchQuery);

  // Update local input value when storage value changes
  React.useEffect(() => {
    setSearchInputValue(searchQuery);
  }, [searchQuery]);

  // Debounce the search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchQuery, debounceMs, setDebouncedSearchQuery]);

  // Handle search input changes
  const handleSearchChange = React.useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setSearchQuery(value);
    },
    [setSearchQuery],
  );

  // Clear search
  const clearSearch = React.useCallback(() => {
    setSearchInputValue("");
    setSearchQuery("");
    setDebouncedSearchQuery("");
  }, [setSearchQuery, setDebouncedSearchQuery]);

  // Set search query directly (for programmatic updates)
  const setSearchQueryDirectly = React.useCallback(
    (value: string) => {
      handleSearchChange(value);
    },
    [handleSearchChange],
  );

  return {
    searchQuery,
    searchInputValue,
    debouncedSearchQuery,
    handleSearchChange,
    clearSearch,
    setSearchQuery: setSearchQueryDirectly,
  };
}
